// src/components/hotelMealPlans/HotelMealPlansTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetHotelMealPlans from "../../hooks/hotelMealPlans/useGetHotelMealPlans";
import useDeleteHotelMealPlan from "../../hooks/hotelMealPlans/useDeleteHotelMealPlan";
import useDeleteAllHotelMealPlans from "../../hooks/hotelMealPlans/useDeleteAllHotelMealPlans";
import useRestoreHotelMealPlan from "../../hooks/hotelMealPlans/useRestoreHotelMealPlan";
import useRestoreAllHotelMealPlans from "../../hooks/hotelMealPlans/useRestoreAllHotelMealPlans";
import useForceDeleteHotelMealPlan from "../../hooks/hotelMealPlans/useForceDeleteHotelMealPlan";
import useForceDeleteAllHotelMealPlans from "../../hooks/hotelMealPlans/useForceDeleteAllHotelMealPlans";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import HotelMealPlansTableRow from "./Table/HotelMealPlansTableRow";
import HotelMealPlansTableColumns from "./Table/HotelMealPlansTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const HotelMealPlansTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("hotel_meal_plans_list"),
            create: has("hotel_meal_plans_create"),
            edit: has("hotel_meal_plans_edit"),
            delete: has("hotel_meal_plans_delete"),
            forceDelete: has("hotel_meal_plans_force_delete"),
            archiveList: has("hotel_meal_plans_archive_list"),
            restore: has("hotel_meal_plans_restore"),
            restoreAll: has("hotel_meal_plans_restore-all-table"),
            deleteAll: has("hotel_meal_plans_delete-all-table"),
            massForceDeleteAll: has("hotel_meal_plans_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/hotel-meal-plans/archive");

    // CRUD Hooks
    const { deleteHotelMealPlans, isDelete } = useDeleteHotelMealPlan();
    const { deleteAllHotelMealPlans, isDeleteAll } = useDeleteAllHotelMealPlans();
    const { restoreHotelMealPlan, isRestore } = useRestoreHotelMealPlan();
    const { restoreAllHotelMealPlans, isRestoreAll } = useRestoreAllHotelMealPlans();
    const { forceDeleteHotelMealPlan, isForceDelete } = useForceDeleteHotelMealPlan();
    const { forceDeleteAllHotelMealPlans, isForceDeleteAll } = useForceDeleteAllHotelMealPlans();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/hotel-meal-plans",
        deleteSingle: deleteHotelMealPlans,
        deleteAll: deleteAllHotelMealPlans,
        restoreSingle: restoreHotelMealPlan,
        restoreAll: restoreAllHotelMealPlans,
        forceDeleteSingle: forceDeleteHotelMealPlan,
        forceDeleteAll: forceDeleteAllHotelMealPlans,
        isArchive,
    });

    // ✨ Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    // ✨ Updated filters to include status and sorting
    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    // ✨ Filter configuration for TableFilterBar
    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    // ✨ Clear all filters handler
    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, hotelMealPlans, meta } = useGetHotelMealPlans(
        page,
        perPage,
        filters
    );

    const items = useMemo(() => hotelMealPlans?.items || [], [hotelMealPlans]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <HotelMealPlansTableRow
            key={params.item.id}
            hotelMealPlan={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive
        ? !perms.massForceDeleteAll
        : !perms.deleteAll;

    const title = isArabic ? "خطط الوجبات" : "Hotel Meal Plans";
    const subtitle = isArchive
        ? isArabic
            ? "عرض خطط الوجبات المحذوفة (الأرشيف)"
            : "Archived hotel meal plans (soft deleted)"
        : isArabic
            ? "إدارة خطط الوجبات المتاحة في النظام"
            : "Manage available hotel meal plans in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة خطة وجبات"
            : "Create new meal plan";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للاستعادة"
                        : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllHotelMealPlans({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للإنشاء"
                        : "No create permission"
                );
                return;
            }

            navigate("/hotel-meal-plans/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/hotel-meal-plans/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/hotel-meal-plans"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={HotelMealPlansTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={
                    isArchive ? "لا توجد خطط وجبات في الأرشيف" : "لا توجد خطط وجبات"
                }
                emptyLabelEn={isArchive ? "No archived hotel meal plans" : "No hotel meal plans"}
                loadErrorAr="حدث خطأ أثناء تحميل خطط الوجبات"
                loadErrorEn="Failed to load hotel meal plans"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default HotelMealPlansTable;
